/**
 * ambience.js — Ambient scene definitions for the music soundboard.
 * Each scene has an array of track URLs. The DM picks a scene and
 * a random track from that scene plays on the Display view.
 */

/* helper to encode filenames with special chars for URL use */
const e = (name) => `/audio/${name}`;

export const AMBIENT_SCENES = [
  {
    id: 'battle', label: 'Battle', icon: '⚔️', color: '#cc4444',
    tracks: [
      e('Battle/08. Battle 1.mp3'),
      e('Battle/10. Sunken Shrine.mp3'),
      e('Battle/11. Dungeon.mp3'),
      e('Battle/13. Battle 2.mp3'),
      e('Battle/13. The Airship.mp3'),
      e('Battle/15. Flying Fortress.mp3'),
      e('Battle/16. Battle.mp3'),
      e('Battle/21. Run!.mp3'),
      e('Battle/28. Battle With the Four Fiends.mp3'),
      e('Battle/29. Miniboss Battle.mp3'),
      e('Battle/30. Boss Battle A..mp3'),
      e('Battle/31. Boss Battle B.mp3'),
      e('Battle/35. Giott, King of the Dwarves.mp3'),
      e('Battle/42. Within the Giant.mp3'),
      e('Battle/46. Epilogue -3-.mp3'),
    ],
  },
  {
    id: 'campfire', label: 'Campfire', icon: '🔥', color: '#e67e22',
    tracks: [
      e('Campfire/01. Prelude.mp3'),
      e('Campfire/01. The Prelude.mp3'),
      e('Campfire/03. Castle Cornelia.mp3'),
      e('Campfire/04. Theme of Love.mp3'),
      e('Campfire/20. Ending Theme.mp3'),
    ],
  },
  {
    id: 'cave', label: 'Cave', icon: '🕯️', color: '#5a4a6a',
    tracks: [
      e('Cave/07. Main Theme of FINAL FANTASY IV.mp3'),
      e('Cave/12. Into the Darkness.mp3'),
      e('Cave/14. Bomb Ring.mp3'),
      e('Cave/22. Derelict Keep.mp3'),
      e('Cave/38. The Land of Summons.mp3'),
      e('Cave/40. Another Moon.mp3'),
    ],
  },
  {
    id: 'combatbegins', label: 'Combat Begins', icon: '🎺', color: '#e74c3c', auto: true,
    tracks: [
      e('Combatbegins/55. Attack!.mp3'),
    ],
  },
  {
    id: 'defeat', label: 'Defeat', icon: '💀', color: '#374151', auto: true,
    tracks: [
      e('Defeat/18. Game Over.mp3'),
    ],
  },
  {
    id: 'dramatic', label: 'Dramatic', icon: '🎭', color: '#8e44ad',
    tracks: [
      e('Dramatic/22. Suspicion.mp3'),
      e('Dramatic/23. Golbez, Clad In Darkness.mp3'),
      e('Dramatic/33. Somewhere In the World....mp3'),
      e('Dramatic/36. Dancing Calcabrena.mp3'),
    ],
  },
  {
    id: 'finalboss', label: 'Final Boss', icon: '👹', color: '#b91c1c',
    tracks: [
      e('FinalBoss/32. Final Battle.mp3'),
      e('FinalBoss/43. The Final Battle.mp3'),
    ],
  },
  {
    id: 'grass', label: 'Overworld', icon: '🌿', color: '#2d7d32',
    tracks: [
      e('Grass/04. Main Theme.mp3'),
      e('Grass/09. The Ship.mp3'),
      e('Grass/44. Epilogue -1-.mp3'),
      e('Grass/45. Epilogue -2-.mp3'),
    ],
  },
  {
    id: 'ice', label: 'Ice', icon: '❄️', color: '#3b82f6',
    tracks: [
      e('Ice/05. Chaos Shrine.mp3'),
      e('Ice/16. Damcyan Castle.mp3'),
      e('Ice/17. Sorrow and Loss.mp3'),
      e('Ice/26. A Long Way to Go.mp3'),
      e('Ice/41. The Lunarians.mp3'),
    ],
  },
  {
    id: 'mines', label: 'Mines', icon: '⛏️', color: '#78716c',
    tracks: [
      e('Mines/02. The Red Wings.mp3'),
      e('Mines/03. Kingdom of Baron.mp3'),
      e('Mines/14. Mount Gulg.mp3'),
      e('Mines/19. Mt. Ordeals.mp3'),
      e('Mines/20. Fabul.mp3'),
      e('Mines/30. Troian Beauty.mp3'),
      e('Mines/34. Land of Dwarves.mp3'),
    ],
  },
  {
    id: 'shop', label: 'Shop', icon: '🛒', color: '#d97706',
    tracks: [
      e('Shop/02. Opening Theme.mp3'),
      e('Shop/07. Town.mp3'),
      e('Shop/12. Menu Screen.mp3'),
    ],
  },
  {
    id: 'shrinechurch', label: 'Shrine', icon: '⛪', color: '#a78bfa',
    tracks: [
      e('ShrineChurch/21. Church.mp3'),
    ],
  },
  {
    id: 'tavern', label: 'Tavern', icon: '🍺', color: '#c4813a',
    tracks: [
      e('Tavern/06. Matoya\'s Cave.mp3'),
      e('Tavern/06. Welcome to Our Town!.mp3'),
      e('Tavern/08. Shop.mp3'),
      e('Tavern/24. Hey, Cid!.mp3'),
      e('Tavern/28. Inn.mp3'),
      e('Tavern/48. Inn.mp3'),
    ],
  },
  {
    id: 'victory', label: 'Victory', icon: '🏆', color: '#4ade80', auto: true,
    tracks: [
      e('Victory/09. Fanfare.mp3'),
      e('Victory/17. Victory.mp3'),
      e('Victory/25. Fanfare.mp3'),
      e('Victory/29. The Airship.mp3'),
      e('Victory/50. Fanfare 1.mp3'),
      e('Victory/51. Fanfare 2.mp3'),
    ],
  },
];

/** Scenes meant for manual selection (not auto-triggered) */
export const MANUAL_SCENES = AMBIENT_SCENES.filter((s) => !s.auto);

export const SCENE_MAP = Object.fromEntries(AMBIENT_SCENES.map((s) => [s.id, s]));

/** Pick a random track from a scene, optionally excluding a specific URL */
export function randomTrack(sceneId, excludeUrl) {
  const scene = SCENE_MAP[sceneId];
  if (!scene || scene.tracks.length === 0) return null;
  const pool = excludeUrl ? scene.tracks.filter((t) => t !== excludeUrl) : scene.tracks;
  if (pool.length === 0) return scene.tracks[0];
  return pool[Math.floor(Math.random() * pool.length)];
}

/** Extract a display name from a track URL */
export function trackName(url) {
  if (!url) return '';
  const file = decodeURIComponent(url.split('/').pop() || '');
  return file.replace(/\.mp3$/i, '').replace(/^\d+\.\s*/, '');
}
